import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {listPageValveManufacturersQuery} from './__generated__/listPageValveManufacturersQuery.graphql';
import {AssetManufacturersOrder} from './__generated__/ValveManufacturersPaginationQuery.graphql';
import {ValveManufacturersTable_valveManufacturers$key} from './__generated__/ValveManufacturersTable_valveManufacturers.graphql';
import {ValveManufacturersTableDeleteValveManufacturerMutation} from './__generated__/ValveManufacturersTableDeleteValveManufacturerMutation.graphql';
import TableFilters from './TableFilters';

type Props = {
  valveManufacturers: ValveManufacturersTable_valveManufacturers$key;
};

type Filters = {
  name?: string;
  orderBy?: AssetManufacturersOrder;
};

function ValveManufacturersTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageValveManufacturersQuery,
    ValveManufacturersTable_valveManufacturers$key
  >(
    graphql`
      fragment ValveManufacturersTable_valveManufacturers on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "AssetManufacturersOrder"}
        )
        @refetchable(queryName: "ValveManufacturersPaginationQuery") {
        valveManufacturers(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "ValveManufacturersList_valveManufacturers") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
            }
          }
        }
      }
    `,
    props.valveManufacturers,
  );

  const [commit] = useMutation<ValveManufacturersTableDeleteValveManufacturerMutation>(
    deleteValveManufacturerMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.valveManufacturers.totalCount}
      columns={columns}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      isTransitioning={isTransitioning}
      data={data.valveManufacturers.edges}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {valveManufacturerId: id}},
      onCompleted: payload => {
        const entry = payload.deleteValveManufacturer?.valveManufacturer;

        if (entry) {
          showEventNotification('removed', 'Manufacturer', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'ValveManufacturersList_valveManufacturers',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default ValveManufacturersTable;

const deleteValveManufacturerMutation = graphql`
  mutation ValveManufacturersTableDeleteValveManufacturerMutation(
    $input: DeleteValveManufacturerInput!
  ) {
    deleteValveManufacturer(input: $input) {
      valveManufacturer {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
];
